/**
 * Authentication service for direct login via backend API
 *
 * Uses HTTP-only cookies for refresh token (XSS protection).
 * Access token is returned in response body for in-memory storage.
 */

import { config } from '@/config'

const API_BASE_URL = config.apiUrl

/**
 * User profile data from the database
 */
export interface UserProfile {
  first_name: string
  last_name: string
  public_email: string | null
  public_phone: string | null
  company_name: string | null
  markets: string[] | null
  agent_type: 'internal' | 'external' | null
  onboarding_completed_at: string | null
  product_tour_completed_at: string | null
  // Qualification fields
  wholesale_intent: string | null
  proof_of_funds_available: string | null
  proof_of_funds_file_key: string | null
  proof_of_funds_file_name: string | null
  hard_money_lender: string | null
  hard_money_lender_explanation: string | null
  annual_purchase_volume: string | null
  agreement_version: string | null
  agreement_accepted_at: string | null
  // SMS opt-in (MRG-1140); null = never asked
  sms_consent: boolean | null
  sms_consent_at: string | null
  // Agent qualification fields (populated at account-request approval for external agents,
  // editable later via the prefill step before ToS acceptance).
  license_number: string | null
  license_state: string | null
  mls_boards: string[] | null
  // External-agent ToS + training (MRG-281). Auto-stamped for internal agents at provision;
  // collected via the in-app onboarding flow for external agents.
  external_agent_terms_accepted_at: string | null
  external_agent_terms_version: string | null
  training_completed_at: string | null
  training_progress: Record<string, { completed_at: string }> | null
}

/**
 * User information from auth endpoints (JWT-based, used by AuthContext)
 */
export interface UserInfo {
  user_id: string
  email: string
  username: string
  roles: string[]
  given_name?: string
  family_name?: string
  full_name?: string
  email_verified: boolean
}

/**
 * Full user info from /users/me endpoint (includes profile)
 */
export interface UserMeResponse {
  user_id: string
  email: string
  roles: string[]
  email_verified: boolean
  profile: UserProfile
}

/**
 * Login response from /auth/login
 */
export interface LoginResponse {
  access_token: string
  expires_in: number
  user: UserInfo
}

/**
 * Refresh response from /auth/refresh
 */
export interface RefreshResponse {
  access_token: string
  expires_in: number
}

/**
 * Error response from auth endpoints
 */
export interface AuthError {
  detail: string
}

/**
 * Auth service error
 */
export class AuthServiceError extends Error {
  constructor(
    message: string,
    public status?: number,
    public token?: string,
    public reason?: string,
    public expires?: number
  ) {
    super(message)
    this.name = 'AuthServiceError'
  }
}

/**
 * Login with email and password
 *
 * Sets HTTP-only cookies for session management.
 * Returns access token for in-memory storage and user info.
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Receive cookies
    body: JSON.stringify({ email, password }),
  })

  if (response.status === 401) {
    const error = (await response.json()) as AuthError
    throw new AuthServiceError(error.detail || 'Invalid email or password', 401)
  }

  if (response.status === 403) {
    const data = await response.json()
    // detail can be a string or an object with message, token, reason, and expires
    const detail = data.detail
    if (typeof detail === 'object' && detail !== null) {
      throw new AuthServiceError(
        detail.message || 'You must change your password.',
        403,
        detail.token,
        detail.reason,
        detail.expires
      )
    }
    throw new AuthServiceError(
      detail || 'You must change your password.',
      403
    )
  }

  if (response.status === 503) {
    throw new AuthServiceError('Authentication service unavailable', 503)
  }

  if (!response.ok) {
    throw new AuthServiceError('Login failed', response.status)
  }

  return response.json()
}

/**
 * Refresh access token using refresh token cookie
 *
 * The refresh token is sent via HTTP-only cookie.
 * Returns new access token for in-memory storage.
 */
export async function refreshToken(): Promise<RefreshResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include', // Send refresh_token cookie
  })

  if (response.status === 401) {
    throw new AuthServiceError('Session expired', 401)
  }

  if (response.status === 503) {
    throw new AuthServiceError('Authentication service unavailable', 503)
  }

  if (!response.ok) {
    throw new AuthServiceError('Token refresh failed', response.status)
  }

  return response.json()
}

/**
 * Session response from /auth/me - same structure as LoginResponse
 */
export interface SessionResponse {
  access_token: string
  expires_in: number
  user: UserInfo
}

/**
 * Get current session from cookie
 *
 * Uses cookies to verify session and return access token for in-memory storage.
 * If access token is expired but refresh token is valid, auto-refreshes.
 */
export async function getCurrentSession(): Promise<SessionResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/me`, {
    method: 'GET',
    credentials: 'include', // Send access_token cookie
  })

  if (response.status === 401) {
    throw new AuthServiceError('Not authenticated', 401)
  }

  if (!response.ok) {
    throw new AuthServiceError('Failed to get session', response.status)
  }

  return response.json()
}

/**
 * Logout and clear auth cookies
 */
export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include', // Send cookies to clear
    })
  } catch {
    // Silently ignore network errors during logout
  }
}

/**
 * Request a password reset email
 *
 * Always succeeds to prevent user enumeration.
 * If the email exists, FusionAuth will send a reset email.
 */
export async function forgotPassword(email: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  })

  // Always consider this a success to prevent user enumeration
  if (response.status === 503) {
    throw new AuthServiceError('Authentication service unavailable', 503)
  }
}

/**
 * Reset password using change password ID from email link
 */
export async function resetPassword(changePasswordId: string, password: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      change_password_id: changePasswordId,
      password,
    }),
  })

  if (response.status === 400) {
    const error = (await response.json()) as AuthError
    throw new AuthServiceError(error.detail || 'Password reset link is invalid or expired', 400)
  }

  if (response.status === 503) {
    throw new AuthServiceError('Authentication service unavailable', 503)
  }

  if (!response.ok) {
    throw new AuthServiceError('Password reset failed', response.status)
  }
}

/**
 * Exchange a magic-link token for an authenticated session.
 *
 * `redirect` is the destination path the user expects to land on after
 * authentication; the backend re-checks it against the path baked into the JWT
 * to prevent a forwarded link from being repurposed.
 *
 * Sets HTTP-only cookies for session management. Returns access token for
 * in-memory storage and user info, same shape as login().
 */
export async function magicLinkLogin(token: string, redirect: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/magic-link`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ token, redirect }),
  })

  if (response.status === 400) {
    const error = (await response.json()) as AuthError
    throw new AuthServiceError(error.detail || 'Invalid magic link', 400)
  }

  if (response.status === 409) {
    const error = (await response.json()) as AuthError
    throw new AuthServiceError(error.detail || 'This magic link has already been used', 409)
  }

  if (response.status === 503) {
    throw new AuthServiceError('Authentication service unavailable', 503)
  }

  if (!response.ok) {
    throw new AuthServiceError('Magic link login failed', response.status)
  }

  return response.json()
}

/**
 * Login with Google ID token
 *
 * Exchanges a Google ID token (from Google Sign-In SDK) for API access tokens.
 * Sets HTTP-only cookies for session management.
 * Returns access token for in-memory storage and user info.
 */
export async function googleLogin(token: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/google`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Receive cookies
    body: JSON.stringify({ token }),
  })

  if (response.status === 401) {
    const error = (await response.json()) as AuthError
    throw new AuthServiceError(error.detail || 'Invalid Google token', 401)
  }

  if (response.status === 503) {
    const error = (await response.json()) as AuthError
    throw new AuthServiceError(error.detail || 'Google sign-in is not available', 503)
  }

  if (!response.ok) {
    throw new AuthServiceError('Google login failed', response.status)
  }

  return response.json()
}
